import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useCrmResyncOpportunity,
  useCrmResyncProgressPercent,
  useGetCrmResyncStatus,
} from '~/hooks/apis/offers/useCrmResyncOpportunity';
import { useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';
import type { OpportunityRelatedRecords } from '~/hooks/apis/offers/useGetOpportunityRelated';

import OffersService from '~/services/OffersService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';
import { isSalesforceOpportunityId } from '~/utils/validateSalesforceId';

import type { PrerequisiteItem, PrerequisiteStatus } from '../PrerequisitesStep';

export type PrerequisiteCheckStatus =
  | 'idle'
  | 'checking'
  | 'ready'
  | 'not-ready'
  | 'not-found'
  | 'wrong-vendor'
  | 'error';

export interface UseOpportunityStepParams {
  isPrerequisitesStep: boolean;
  vendorId: string;
}

export interface PrerequisitesReadyResult {
  hasExistingPropertyLink: boolean;
  opportunityRelated: OpportunityRelatedRecords | undefined;
  ready: true;
  validatedOpportunity: App.OpportunityPage;
}

export interface PrerequisitesNotReadyResult {
  ready: false;
}

function hasValidDealOptions(deals?: Array<unknown>): boolean {
  return !!deals?.length;
}

// Every Deal Option needs a Room Asset: the Rooms step maps assets to room types, so a Deal Option
// without one would have no room to link to.
function getDealNamesWithoutRoomAsset(deals: Array<{ name?: string | null; room_asset?: unknown }> = []) {
  return deals.filter((deal) => !deal.room_asset).map((deal) => deal.name || 'Unnamed Deal Option');
}

/**
 * Owns the Salesforce ID + Prerequisites steps (0-1): kicking off/tracking the CRM resync,
 * checking the opportunity is valid/ready, and handing back the validated opportunity data
 * once ready so later steps (Property/Rate Plan/Rooms) can seed themselves from it.
 */
export function useOpportunityStep({ isPrerequisitesStep, vendorId }: UseOpportunityStepParams) {
  const [salesforceOfferId, setSalesforceOfferId] = useState('');
  const [prerequisiteCheckStatus, setPrerequisiteCheckStatus] = useState<PrerequisiteCheckStatus>('idle');
  const [validatedOpportunity, setValidatedOpportunity] = useState<App.OpportunityPage | undefined>(undefined);
  const [preloadedOpportunityRelated, setPreloadedOpportunityRelated] = useState<OpportunityRelatedRecords | undefined>(
    undefined,
  );
  const [resyncRequestedAt, setResyncRequestedAt] = useState<string | undefined>(undefined);
  const [isValidatingOpportunity, setIsValidatingOpportunity] = useState(false);
  const validationRunId = useRef(0);
  const prerequisiteCheckStartedForRef = useRef<string | undefined>(undefined);

  const trimmedSalesforceOfferId = salesforceOfferId.trim();

  const { refetch: refetchOpportunity } = useGetOpportunity(trimmedSalesforceOfferId);
  const { refetch: refetchOpportunityRelated } = useGetOpportunityRelated(trimmedSalesforceOfferId, { enabled: false });
  const crmResyncMutation = useCrmResyncOpportunity();
  const { data: resyncStatus, isError: isResyncStatusError } = useGetCrmResyncStatus(
    trimmedSalesforceOfferId,
    resyncRequestedAt,
    { enabled: !!resyncRequestedAt },
  );
  const hasResyncFailed = isResyncStatusError || resyncStatus?.status === 'failed';
  const isResyncComplete = resyncStatus?.status === 'complete';
  const isWaitingForResync = isPrerequisitesStep && !isResyncComplete && !hasResyncFailed;
  const resyncProgressPercent = useCrmResyncProgressPercent(resyncRequestedAt, isWaitingForResync);
  const isPrerequisitesStepNextDisabled = isPrerequisitesStep && prerequisiteCheckStatus !== 'ready';

  useEffect(() => {
    if (!isPrerequisitesStep || !isResyncComplete || prerequisiteCheckStartedForRef.current === resyncRequestedAt) {
      return;
    }

    prerequisiteCheckStartedForRef.current = resyncRequestedAt;

    let cancelled = false;
    setPrerequisiteCheckStatus('checking');

    (async () => {
      try {
        const { data: opportunity, error } = await refetchOpportunity();

        if (cancelled) {
          return;
        }

        if (error || !opportunity?.account_18_digit_id__c) {
          setPrerequisiteCheckStatus('not-found');
          return;
        }

        if (opportunity.account_18_digit_id__c !== vendorId) {
          setPrerequisiteCheckStatus('wrong-vendor');
          return;
        }

        setValidatedOpportunity(opportunity);

        const { data: opportunityRelated } = await refetchOpportunityRelated();

        if (cancelled) {
          return;
        }

        setPreloadedOpportunityRelated(opportunityRelated);
        const hasDealOption = hasValidDealOptions(opportunityRelated?.deals);
        const hasAllRoomAssets = getDealNamesWithoutRoomAsset(opportunityRelated?.deals).length === 0;
        setPrerequisiteCheckStatus(hasDealOption && hasAllRoomAssets ? 'ready' : 'not-ready');
      } catch (error) {
        if (cancelled) {
          return;
        }

        reportError(error);
        setPrerequisiteCheckStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isPrerequisitesStep,
    isResyncComplete,
    resyncRequestedAt,
    refetchOpportunity,
    refetchOpportunityRelated,
    vendorId,
  ]);

  const prerequisiteItems: Array<PrerequisiteItem> = (() => {
    const hasPrerequisiteCheckFailed =
      hasResyncFailed ||
      prerequisiteCheckStatus === 'not-found' ||
      prerequisiteCheckStatus === 'wrong-vendor' ||
      prerequisiteCheckStatus === 'error';
    const hasPrerequisiteCheckFinished = prerequisiteCheckStatus === 'ready' || prerequisiteCheckStatus === 'not-ready';

    const statusFor = (isFactTrue: boolean): PrerequisiteStatus => {
      if (hasPrerequisiteCheckFailed) {
        return 'unmet';
      }

      if (hasPrerequisiteCheckFinished) {
        return isFactTrue ? 'met' : 'unmet';
      }

      return 'pending';
    };

    const opportunityStatus = statusFor(true);
    const opportunityDetail =
      prerequisiteCheckStatus === 'not-found'
        ? "This Salesforce offer ID couldn't be found. Double-check the ID and try again."
        : prerequisiteCheckStatus === 'wrong-vendor'
          ? 'This Salesforce offer does not belong to this vendor.'
          : undefined;

    const deals = preloadedOpportunityRelated?.deals ?? [];
    const hasDealOption = hasValidDealOptions(deals);
    const dealNamesWithoutRoomAsset = getDealNamesWithoutRoomAsset(deals);
    const hasAllRoomAssets = dealNamesWithoutRoomAsset.length === 0;

    const dealOptionDetail =
      'No Deal Option is linked to this Opportunity in Salesforce yet. Add at least one Deal Option.';
    const roomAssetDetail = [
      `${dealNamesWithoutRoomAsset.length} Deal Option${dealNamesWithoutRoomAsset.length === 1 ? ' isn' : 's aren'}'t linked to a Room Asset in Salesforce. Set Room_Asset__c on each:`,
      ...dealNamesWithoutRoomAsset.map((name) => `- ${name}`),
    ].join('\n');

    return [
      { label: 'A valid Opportunity in Salesforce', status: opportunityStatus, detail: opportunityDetail },
      {
        label: 'Linked to at least 1 valid Deal Option',
        status: statusFor(hasDealOption),
        detail: dealOptionDetail,
      },
      {
        label: 'Every Deal Option linked to a valid Room Asset',
        status: statusFor(hasAllRoomAssets),
        detail: roomAssetDetail,
      },
    ];
  })();

  const retryResync = useCallback(async () => {
    if (!trimmedSalesforceOfferId) {
      return { errorMessage: undefined };
    }

    try {
      const resyncResponse = await crmResyncMutation.mutateAsync(trimmedSalesforceOfferId);
      setResyncRequestedAt(resyncResponse.requestedAt);
      setPrerequisiteCheckStatus('idle');
      return { errorMessage: undefined };
    } catch (resyncError) {
      return { errorMessage: `Unable to start a Salesforce resync. ${getErrorMessage(resyncError)}` };
    }
  }, [crmResyncMutation, trimmedSalesforceOfferId]);

  async function handleSalesforceIdNext(): Promise<{ errorMessage?: string; success: boolean }> {
    if (!isSalesforceOpportunityId(trimmedSalesforceOfferId)) {
      return { errorMessage: 'Enter a valid Salesforce offer ID.', success: false };
    }

    const runId = validationRunId.current + 1;
    validationRunId.current = runId;
    setIsValidatingOpportunity(true);

    // existence/vendor-ownership validation deferred to Prerequisites/valid-opportunity rule
    try {
      const resyncResponse = await crmResyncMutation.mutateAsync(trimmedSalesforceOfferId);
      setResyncRequestedAt(resyncResponse.requestedAt);
    } catch (resyncError) {
      setIsValidatingOpportunity(false);
      return { errorMessage: `Unable to start a Salesforce resync. ${getErrorMessage(resyncError)}`, success: false };
    }

    if (runId !== validationRunId.current) {
      return { success: false };
    }

    setPrerequisiteCheckStatus('idle');
    setValidatedOpportunity(undefined);
    setPreloadedOpportunityRelated(undefined);
    setIsValidatingOpportunity(false);
    return { success: true };
  }

  async function handlePrerequisitesNext(): Promise<PrerequisitesReadyResult | PrerequisitesNotReadyResult> {
    if (prerequisiteCheckStatus !== 'ready' || !validatedOpportunity) {
      return { ready: false };
    }

    const { result: existingOffer } = await OffersService.getOffer(trimmedSalesforceOfferId).catch(() => ({
      result: undefined,
    }));

    return {
      hasExistingPropertyLink: !!existingOffer?.packages?.some((pkg: App.BasePackage) => pkg?.fk_property_id),
      opportunityRelated: preloadedOpportunityRelated,
      ready: true,
      validatedOpportunity,
    };
  }

  // Full reset for starting a fresh wizard run (handleOpen).
  function reset() {
    resetBusyState();
    prerequisiteCheckStartedForRef.current = undefined;
    setValidatedOpportunity(undefined);
    setPreloadedOpportunityRelated(undefined);
    setSalesforceOfferId('');
  }

  // Narrower reset for just closing the dialog (handleConfirmClose) -- leaves the validated
  // opportunity data alone since a subsequent handleOpen() always calls the full reset() anyway.
  function resetBusyState() {
    validationRunId.current += 1;
    setIsValidatingOpportunity(false);
    setResyncRequestedAt(undefined);
    setPrerequisiteCheckStatus('idle');
  }

  return {
    handlePrerequisitesNext,
    handleSalesforceIdNext,
    hasResyncFailed,
    isPrerequisitesStepNextDisabled,
    isResyncComplete,
    isValidatingOpportunity,
    isWaitingForResync,
    prerequisiteCheckStatus,
    prerequisiteItems,
    preloadedOpportunityRelated,
    reset,
    resetBusyState,
    resyncFailureMessage: resyncStatus?.message ?? undefined,
    resyncProgressPercent,
    retryResync,
    salesforceOfferId,
    setSalesforceOfferId,
    trimmedSalesforceOfferId,
    validatedOpportunity,
  };
}
